import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';

const WHATSAPP_NUM = '971547172715';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { totalItems } = useCart();
  const { user, logout, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'الرئيسية', path: '/' },
    { label: 'المنتجات', path: '/products' },
    { label: 'أفلام ومسلسلات', path: '/movies' },
    { label: 'تتبّع طلبك', path: '/track-order' },
    { label: 'تواصل معنا', path: '/contact' },
    { label: 'الأسئلة الشائعة', path: '/faq' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-black/95 backdrop-blur-md border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      {/* Top accent line */}
      {scrolled && (
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8721A]/60 to-transparent" />
      )}

      <div className="px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <div className="w-11 h-11 rounded-none overflow-hidden flex-shrink-0 border border-[#E8721A]/30 group-hover:border-[#E8721A]/70 transition-colors duration-300">
              <img
                src="https://storage.readdy-site.link/project_files/a40bf9e1-c450-4b0e-a3b5-5bb7e13c393a/18cd78e4-383a-4686-8136-4621e647f81f_4146cd7d-a6eb-4f04-b4f5-dcdd15d682a9.jpg?v=0f7ee56b4689600540385a34f1cd6c7f"
                alt="نجاحات لتجارة العطور"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-base font-black tracking-tight text-white">
                نجاحات
              </span>
              <span className="text-[10px] font-semibold text-[#E8721A]/70 tracking-widest uppercase whitespace-nowrap">
                لتجارة العطور ومستحضرات التجميل
              </span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs font-semibold tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap relative group ${
                  isActive(link.path)
                    ? 'text-[#E8721A]'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#E8721A]" />
                )}
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#E8721A]/0 group-hover:bg-[#E8721A]/40 transition-all duration-300" />
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {/* WhatsApp */}
            <a
              href={`https://wa.me/${WHATSAPP_NUM}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="hidden md:flex items-center gap-2 text-[#25D366]/70 hover:text-[#25D366] transition-colors cursor-pointer"
              title="تواصل عبر واتساب"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-whatsapp-line text-xl" />
              </div>
            </a>

            {/* Cart */}
            <button
              onClick={() => navigate('/payment')}
              className="relative cursor-pointer group"
              aria-label="سلة التسوق"
            >
              <div className="w-9 h-9 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-xl text-white/70 group-hover:text-white transition-colors" />
              </div>
              {totalItems > 0 && (
                <span className="absolute -top-1 -left-1 w-5 h-5 flex items-center justify-center bg-[#E8721A] text-white text-xs font-bold" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
                  {totalItems}
                </span>
              )}
            </button>

            {/* Login/User */}
            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                <span className="hidden md:block text-xs font-semibold text-white/60 tracking-widest">
                  {user?.name}
                </span>
                <button
                  onClick={logout}
                  className="border border-[#E8721A]/50 text-[#E8721A] px-4 py-2 text-xs font-bold hover:bg-[#E8721A] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap tracking-widest uppercase"
                >
                  خروج
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="border border-[#E8721A]/50 text-[#E8721A] px-4 py-2 text-xs font-bold hover:bg-[#E8721A] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap tracking-widest uppercase"
              >
                دخول
              </Link>
            )}

            {/* Mobile menu toggle */}
            <button
              className="lg:hidden cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="القائمة"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-white/80`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-black/98 border-t border-white/5 py-4 backdrop-blur-md">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`block px-4 py-3 text-xs font-bold tracking-widest uppercase transition-colors cursor-pointer ${
                  isActive(link.path) ? 'text-[#E8721A]' : 'text-white/50 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={`https://wa.me/${WHATSAPP_NUM}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 px-4 py-3 text-[#25D366] text-xs font-bold tracking-widest cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line" />
              </div>
              واتساب: {WHATSAPP_NUM}+
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}
